import { useMemo, useState } from 'react';

const onUnique = (values = []) => [...new Set(values.filter(Boolean))];

const onSystemMessage = (text, senderId = null, event = 'system') => ({
    id: `system-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    senderId,
    text,
    createdAt: new Date().toISOString(),
    status: 'read',
    system: true, event,
});

const onTicketToConversation = (ticket = {}, currentUserId = null) => {
    const MESSAGE_MEMBER_IDS = (ticket.messages ?? []).map((message) => message.senderId).filter(Boolean);
    const MEMBER_IDS = onUnique([currentUserId, ticket.assigneeId, ...MESSAGE_MEMBER_IDS]);
    return {
        id: ticket.id,
        name: `${ticket.code ?? ''}${ticket.code ? ' · ' : ''}${ticket.subject ?? 'Contacto call center'}`,
        type: 'group',
        memberIds: MEMBER_IDS,
        adminIds: onUnique([ticket.assigneeId, currentUserId]),
        unread: Number(ticket.unread ?? 0),
        mentions: Number(ticket.mentions ?? 0),
        pinned: Boolean(ticket.pinned),
        invitations: [],
        image: ticket.image ?? null,
        typingUserIds: ticket.typingUserIds ?? [],
        pinnedMessageId: ticket.pinnedMessageId ?? null,
        messages: (ticket.messages ?? []).map((message) => ({
            ...message,
            hiddenFor: message.hiddenFor ?? message.hiddenForUserIds ?? [],
        })),
        callCenterMeta: { ...ticket },
    };
};

export const useCallCenterChat = ({ calls = [], contacts = [], tickets = [], currentUserId = null, initialCallId = null, initialTicketId = null } = {}) => {
    const SOURCE = calls.length ? calls : (contacts.length ? contacts : tickets);
    const conversations = SOURCE.map((ticket) => onTicketToConversation(ticket, currentUserId));
    const initialConversationId = initialCallId ?? initialTicketId;

    const [useNameConversationsState, setNameConversationsState] = useState(conversations);
    const [useNameSelectedConversationIdState, setNameSelectedConversationIdState] = useState(
        initialConversationId ?? conversations[0]?.id ?? null,
    );

    const memo__selectedConversationMemo = useMemo(() => {
        return useNameConversationsState.find((conversation) => conversation.id === useNameSelectedConversationIdState) ?? null;
    }, [useNameConversationsState, useNameSelectedConversationIdState]);

    const cb__selectConversationCallback = (conversationId) => {
        setNameSelectedConversationIdState(conversationId);
        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id === conversationId
                ? { ...conversation, unread: 0 }
                : conversation
        )));
    };

    const cb__clearSelectionCallback = () => {
        setNameSelectedConversationIdState(null);
    };

    const cb__sendMessageCallback = ({ text = '', attachments = [] } = {}) => {
        const VALUE = String(text ?? '').trim();
        const FILES = Array.isArray(attachments) ? attachments : [];
        if ((!VALUE && FILES.length === 0) || !useNameSelectedConversationIdState) return null;

        const SELECTED = useNameConversationsState.find((conversation) => conversation.id === useNameSelectedConversationIdState);
        if (SELECTED?.type === 'group' && (SELECTED.leftBy ?? []).includes(currentUserId)) return null;

        const MESSAGE = {
            id: `local-${Date.now()}`,
            senderId: currentUserId,
            text: VALUE,
            attachments: FILES,
            createdAt: new Date().toISOString(),
            status: 'sent',
        };

        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id === useNameSelectedConversationIdState
                ? { ...conversation, messages: [...(conversation.messages ?? []), MESSAGE] }
                : conversation
        )));

        return MESSAGE;
    };

    const cb__togglePinCallback = (conversationId) => {
        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id === conversationId ? { ...conversation, pinned: !conversation.pinned } : conversation
        )));
    };

    const cb__togglePinnedMessageCallback = (conversationId, messageId) => {
        if (!conversationId || !messageId) return false;
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            const EXISTS = (conversation.messages ?? []).some((message) => message.id === messageId && !message.system);
            if (!EXISTS) return conversation;
            CHANGED = true;
            return { ...conversation, pinnedMessageId: conversation.pinnedMessageId === messageId ? null : messageId };
        }));
        return CHANGED;
    };

    const cb__setTypingUsersCallback = (conversationId, userIds = []) => {
        if (!conversationId) return false;
        const IDS = onUnique(Array.isArray(userIds) ? userIds : []);
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = true;
            return { ...conversation, typingUserIds: IDS };
        }));
        return CHANGED;
    };

    const cb__renameConversationCallback = (conversationId, name) => {
        const VALUE = String(name ?? '').trim();
        if (!VALUE) return false;
        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id === conversationId ? { ...conversation, name: VALUE } : conversation
        )));
        return true;
    };

    const cb__updateConversationImageCallback = (conversationId, image = null) => {
        if (!conversationId) return false;
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = conversation.image?.url !== image?.url;
            return { ...conversation, image };
        }));
        return CHANGED;
    };

    const cb__createGroupCallback = ({ name, memberIds = [], creatorName = 'Un participante', image = null } = {}) => {
        const VALUE = String(name ?? '').trim();
        const MEMBERS = onUnique([currentUserId, ...memberIds]);
        if (!VALUE || MEMBERS.length < 3) return null;

        const CONVERSATION = {
            id: `group-${Date.now()}`,
            name: VALUE,
            type: 'group',
            memberIds: MEMBERS,
            adminIds: currentUserId ? [currentUserId] : [],
            unread: 0,
            mentions: 0,
            pinned: false,
            invitations: [],
            image,
            messages: [onSystemMessage(`${creatorName} creó el grupo “${VALUE}”.`, currentUserId, 'group-created')],
        };

        setNameConversationsState((current) => [CONVERSATION, ...current]);
        setNameSelectedConversationIdState(CONVERSATION.id);
        return CONVERSATION;
    };

    const cb__addParticipantCallback = (conversationId, userId) => {
        if (!userId) return false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            if ((conversation.memberIds ?? []).includes(userId)) return conversation;
            return {
                ...conversation,
                memberIds: onUnique([...(conversation.memberIds ?? []), userId]),
                messages: [...(conversation.messages ?? []), onSystemMessage('Se agregó un participante.', currentUserId)],
            };
        }));
        return true;
    };

    const cb__removeParticipantCallback = (conversationId, userId) => {
        if (!userId || userId === currentUserId) return false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                memberIds: (conversation.memberIds ?? []).filter((id) => id !== userId),
                adminIds: (conversation.adminIds ?? []).filter((id) => id !== userId),
                messages: [...(conversation.messages ?? []), onSystemMessage('Se quitó un participante.', currentUserId)],
            };
        }));
        return true;
    };

    const cb__inviteParticipantCallback = (conversationId, email) => {
        const VALUE = String(email ?? '').trim().toLowerCase();
        if (!VALUE || !VALUE.includes('@')) return false;
        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id === conversationId
                ? { ...conversation, invitations: onUnique([...(conversation.invitations ?? []), VALUE]) }
                : conversation
        )));
        return true;
    };

    const cb__transferAdminCallback = (conversationId, userId) => {
        if (!userId) return false;
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId || !(conversation.memberIds ?? []).includes(userId)) return conversation;
            CHANGED = true;
            return {
                ...conversation,
                adminIds: onUnique([...(conversation.adminIds ?? []), userId]),
                messages: [...(conversation.messages ?? []), onSystemMessage('Se asignó un nuevo administrador.', currentUserId)],
            };
        }));
        return CHANGED;
    };

    const cb__leaveGroupCallback = (conversationId) => {
        const CONVERSATION = useNameConversationsState.find((item) => item.id === conversationId);
        if (!CONVERSATION || CONVERSATION.type !== 'group') return { ok: false, reason: 'not-group' };

        const ADMIN_IDS = CONVERSATION.adminIds ?? [];
        const IS_ADMIN = ADMIN_IDS.includes(currentUserId);
        const OTHER_ADMINS = ADMIN_IDS.filter((id) => id !== currentUserId);
        const OTHER_MEMBERS = (CONVERSATION.memberIds ?? []).filter((id) => id !== currentUserId);

        if (IS_ADMIN && OTHER_ADMINS.length === 0 && OTHER_MEMBERS.length > 0) {
            return { ok: false, reason: 'transfer-admin' };
        }

        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                memberIds: (conversation.memberIds ?? []).filter((id) => id !== currentUserId),
                adminIds: (conversation.adminIds ?? []).filter((id) => id !== currentUserId),
                leftBy: onUnique([...(conversation.leftBy ?? []), currentUserId]),
                unread: 0, mentions: 0,
                messages: [...(conversation.messages ?? []), onSystemMessage('Saliste del grupo.', currentUserId, 'group-left')],
            };
        }));
        return { ok: true };
    };


    const cb__archiveConversationCallback = (conversationId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = true;
            return { ...conversation, archived: true };
        }));
        if (useNameSelectedConversationIdState === conversationId) setNameSelectedConversationIdState(null);
        return CHANGED;
    };

    const cb__deleteConversationCallback = (conversationId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.filter((conversation) => {
            if (conversation.id !== conversationId) return true;
            CHANGED = true;
            return false;
        }));
        if (useNameSelectedConversationIdState === conversationId) setNameSelectedConversationIdState(null);
        return CHANGED;
    };

    const cb__archiveMessageCallback = (conversationId, messageId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                messages: (conversation.messages ?? []).map((message) => {
                    if (message.id !== messageId || message.system) return message;
                    CHANGED = true;
                    return { ...message, archived: true };
                }),
            };
        }));
        return CHANGED;
    };

    const cb__deleteMessageCallback = (conversationId, messageId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                messages: (conversation.messages ?? []).filter((message) => {
                    if (message.id !== messageId || message.system) return true;
                    CHANGED = true;
                    return false;
                }),
            };
        }));
        return CHANGED;
    };

    const cb__deleteMessageForMeCallback = (conversationId, messageId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                messages: (conversation.messages ?? []).map((message) => {
                    if (message.id !== messageId || message.system) return message;
                    CHANGED = true;
                    return { ...message, hiddenFor: onUnique([...(message.hiddenFor ?? []), currentUserId]) };
                }),
            };
        }));
        return CHANGED;
    };

    const cb__deleteMessageForAllCallback = (conversationId, messageId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            return {
                ...conversation,
                messages: (conversation.messages ?? []).filter((message) => {
                    if (message.id !== messageId || message.system || message.senderId !== currentUserId) return true;
                    CHANGED = true;
                    return false;
                }),
            };
        }));
        return CHANGED;
    };

    const cb__clearConversationForMeCallback = (conversationId) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = true;
            return {
                ...conversation,
                unread: 0,
                mentions: 0,
                messages: (conversation.messages ?? []).map((message) => ({
                    ...message,
                    hiddenFor: onUnique([...(message.hiddenFor ?? []), currentUserId]),
                })),
            };
        }));
        return CHANGED;
    };

    const cb__setMessageStatusCallback = (conversationId, messageId, status) => {
        setNameConversationsState((current) => current.map((conversation) => (
            conversation.id !== conversationId ? conversation : {
                ...conversation,
                messages: (conversation.messages ?? []).map((message) => (
                    message.id === messageId ? { ...message, status } : message
                )),
            }
        )));
    };

    const cb__updateTicketCallback = (conversationId, patch = {}) => {
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = true;
            const NEXT_META = { ...(conversation.callCenterMeta ?? {}), ...patch, updatedAt: new Date().toISOString() };
            return {
                ...conversation,
                name: `${NEXT_META.code ?? ''}${NEXT_META.code ? ' · ' : ''}${NEXT_META.subject ?? 'Contacto call center'}`,
                callCenterMeta: NEXT_META,
            };
        }));
        return CHANGED;
    };

    const cb__addCallEventCallback = (conversationId, text) => {
        const VALUE = String(text ?? '').trim();
        if (!VALUE) return false;
        let CHANGED = false;
        setNameConversationsState((current) => current.map((conversation) => {
            if (conversation.id !== conversationId) return conversation;
            CHANGED = true;
            return {
                ...conversation,
                messages: [...(conversation.messages ?? []), onSystemMessage(VALUE, currentUserId, 'support-event')],
            };
        }));
        return CHANGED;
    };

    return {
        conversations: useNameConversationsState,
        selectedConversationId: useNameSelectedConversationIdState,
        selectedConversation: memo__selectedConversationMemo,
        selectConversation: cb__selectConversationCallback,
        clearSelection: cb__clearSelectionCallback,
        sendMessage: cb__sendMessageCallback,
        togglePin: cb__togglePinCallback,
        togglePinnedMessage: cb__togglePinnedMessageCallback,
        setTypingUsers: cb__setTypingUsersCallback,
        renameConversation: cb__renameConversationCallback,
        updateConversationImage: cb__updateConversationImageCallback,
        createGroup: cb__createGroupCallback,
        addParticipant: cb__addParticipantCallback,
        removeParticipant: cb__removeParticipantCallback,
        inviteParticipant: cb__inviteParticipantCallback,
        transferAdmin: cb__transferAdminCallback,
        leaveGroup: cb__leaveGroupCallback,
        archiveConversation: cb__archiveConversationCallback,
        deleteConversation: cb__deleteConversationCallback,
        archiveMessage: cb__archiveMessageCallback,
        deleteMessage: cb__deleteMessageCallback,
        deleteMessageForMe: cb__deleteMessageForMeCallback,
        deleteMessageForAll: cb__deleteMessageForAllCallback,
        clearConversationForMe: cb__clearConversationForMeCallback,
        setMessageStatus: cb__setMessageStatusCallback,
        updateTicket: cb__updateTicketCallback,
        addCallEvent: cb__addCallEventCallback,
        calls: useNameConversationsState.map((conversation) => conversation.callCenterMeta ?? {}),
        contacts: useNameConversationsState.map((conversation) => conversation.callCenterMeta ?? {}),
        tickets: useNameConversationsState.map((conversation) => conversation.callCenterMeta ?? {}),
        selectedCall: memo__selectedConversationMemo?.callCenterMeta ?? null,
        selectedContact: memo__selectedConversationMemo?.callCenterMeta ?? null,
        selectedTicket: memo__selectedConversationMemo?.callCenterMeta ?? null,
        selectedCallId: useNameSelectedConversationIdState,
        selectedContactId: useNameSelectedConversationIdState,
        selectedTicketId: useNameSelectedConversationIdState,
        selectCall: cb__selectConversationCallback,
        selectContact: cb__selectConversationCallback,
        selectTicket: cb__selectConversationCallback,
        closeCallView: cb__clearSelectionCallback,
        closeContactView: cb__clearSelectionCallback,
        closeTicketView: cb__clearSelectionCallback,
        setConversations: setNameConversationsState,
    };
};
